import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import cron from "cron-validate";
import { useSnackbar } from "notistack";
import type React from "react";
import { useEffect, useState } from "react";
import { useApiMutation } from "../../hooks";
import { useApiQuery } from "../../hooks/use-api-query";
import { STATUS_COLORS } from "../../theme/theme";
import {
  type AiRequest,
  type AiResponse,
  CHECK_SCHEDULE_FAILURE_STRATEGIES,
  type Check,
  type CheckSchedule,
  type CheckScheduleFailureStrategy,
  type CheckScheduleSchema,
} from "../../types";
import { AppConfigSelect } from "../app-config-select";

export interface CheckScheduleDialogProps {
  open: boolean;
  mode: "create" | "edit";
  onClose: () => void;
  onSave: (data: CheckScheduleSchema) => void;
  data: CheckScheduleSchema | CheckSchedule;
  setData: (data: CheckScheduleSchema | CheckSchedule | null) => void;
  isProcessing: boolean;
}

// Common timezones
const TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Dubai",
  "Australia/Sydney",
  "Pacific/Auckland",
];

interface ChecksListResponse {
  checks: Check[];
  total: number;
}

const CheckScheduleDialog: React.FC<CheckScheduleDialogProps> = ({
  open,
  mode,
  onClose,
  onSave,
  data,
  setData,
  isProcessing,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [aiText, setAiText] = useState<string | null>(null);

  const title = mode === "create" ? "New Check Schedule" : "Edit Check Schedule";
  const buttonText = mode === "create" ? "Create" : "Save";
  const description =
    mode === "create"
      ? "Create a schedule to run multiple checks on a recurring basis."
      : "Edit your check schedule configuration.";

  // Fetch checks for the selected app config
  const { data: checksResponse } = useApiQuery<ChecksListResponse>(
    data.app_config_id ? `/checks?app_config_id=${data.app_config_id}` : "/checks",
    {
      enabled: !!data.app_config_id,
    },
  );

  const availableChecks = checksResponse?.checks ?? [];

  const {
    mutate: ai,
    data: aiData,
    isPending: isAiProcessing,
    reset,
  } = useApiMutation<AiRequest, AiResponse>("POST", "/ai/response");

  useEffect(() => {
    if (aiData?.resp) {
      if (!cron(aiData.resp).isValid()) {
        enqueueSnackbar(aiData.resp, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        return;
      }

      setAiText(null);

      setData({
        ...data,
        cron_expression: aiData.resp,
      });

      enqueueSnackbar("CRON expression generated successfully", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      reset();
    }
  }, [aiData, setData, data, enqueueSnackbar, reset]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleEnabledChange = (event: SelectChangeEvent) => {
    setData({
      ...data,
      enabled: event.target.value === "on",
    });
  };

  const handleTimezoneChange = (event: SelectChangeEvent) => {
    setData({
      ...data,
      timezone: event.target.value,
    });
  };

  const handleCheckIdsChange = (_: React.SyntheticEvent, value: Check[]) => {
    setData({
      ...data,
      check_ids: value.map((c) => c.id),
    });
  };

  const handleFailureStrategyChange = (event: SelectChangeEvent) => {
    setData({
      ...data,
      failure_strategy: event.target.value as CheckScheduleFailureStrategy,
    });
  };

  const FAILURE_STRATEGY_LABELS: Record<CheckScheduleFailureStrategy, string> = {
    stop_on_failure: "Stop on failure — skip checks that depend on a failed check",
    continue_on_failure: "Continue on failure — run every check regardless",
  };

  const selectedChecks = availableChecks.filter((c) => (data.check_ids || []).includes(c.id));

  const isValid = data.name && data.app_config_id && data.cron_expression;
  const cronValid =
    !data.cron_expression || data.cron_expression === "" || cron(data.cron_expression).isValid();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{description}</DialogContentText>
        <Stack direction="column" spacing={2} alignItems="stretch" justifyContent="center" mt={2}>
          {/* Basic Info */}
          <TextField
            autoFocus
            required
            id="name"
            name="name"
            label="Name"
            placeholder="Daily API Health Checks"
            size="small"
            type="text"
            fullWidth
            value={data.name}
            onChange={handleChange}
          />

          <TextField
            id="description"
            name="description"
            label="Description"
            placeholder="Runs all production health checks every hour..."
            size="small"
            multiline
            minRows={2}
            maxRows={4}
            type="text"
            fullWidth
            value={data.description || ""}
            onChange={handleChange}
          />

          <AppConfigSelect
            label="App Configuration"
            value={data.app_config_id || null}
            onChange={(id) =>
              setData({
                ...data,
                app_config_id: id || "",
                check_ids: [], // Reset check selection when app config changes
              })
            }
            size="small"
            helperText="Select the app configuration for this schedule"
          />

          {/* Check Selection */}
          <Typography variant="subtitle2" fontWeight={600} mt={2}>
            Select Checks
          </Typography>

          <Autocomplete
            multiple
            size="small"
            options={availableChecks}
            value={selectedChecks}
            onChange={handleCheckIdsChange}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={!data.app_config_id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  data.app_config_id
                    ? "Select checks to include..."
                    : "Select an app configuration first"
                }
                size="small"
                helperText={`${selectedChecks.length} check(s) selected`}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.name}
                  size="small"
                  sx={{
                    fontSize: "0.75rem",
                    height: 22,
                    backgroundColor: STATUS_COLORS.info.bg,
                    color: STATUS_COLORS.info.text,
                  }}
                />
              ))
            }
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  fontSize: "0.875rem",
                  display: "flex",
                  alignItems: "center",
                  py: 0.5,
                }}
              >
                <Chip
                  label={option.check_type.toUpperCase()}
                  size="small"
                  sx={{
                    fontSize: "0.65rem",
                    height: 18,
                    mr: 1,
                    backgroundColor: "grey.100",
                  }}
                />
                {option.name}
              </Box>
            )}
          />

          {/* Schedule Configuration */}
          <Typography variant="subtitle2" fontWeight={600} mt={2}>
            Schedule Configuration
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                required
                id="cron_expression"
                name="cron_expression"
                label="Schedule CRON"
                placeholder="E.g. 0 * * * * for hourly"
                size="small"
                type="text"
                fullWidth
                error={!cronValid}
                helperText={!cronValid ? "Invalid CRON expression" : ""}
                value={data.cron_expression || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Select
                fullWidth
                size="small"
                value={data.enabled !== false ? "on" : "off"}
                onChange={handleEnabledChange}
              >
                <MenuItem value="on">Enabled</MenuItem>
                <MenuItem value="off">Disabled</MenuItem>
              </Select>
            </Grid>
          </Grid>

          <TextField
            id="ai_cron"
            label="What is your recurrence?"
            placeholder="E.g. Every hour, Every day at midnight"
            size="small"
            type="text"
            fullWidth
            helperText="Don't know CRON? Write the recurrence you want and press Enter"
            disabled={isAiProcessing}
            value={aiText || ""}
            onChange={(e) => setAiText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && aiText) {
                e.preventDefault();
                ai({
                  query: `Generate a valid CRON expression for the following recurrence "${aiText}". Do not ask any questions, just provide the CRON expression. Feel free to make big assumptions to generate the CRON expression, or just guess something that is close enough. Only respond with the CRON expression and nothing else.`,
                  output_model: {
                    type: "string",
                    name: "cron",
                    description: "Just the VALID CRON expression, no explanation",
                  },
                });
              }
            }}
          />

          <Select
            fullWidth
            size="small"
            value={data.timezone || "UTC"}
            onChange={handleTimezoneChange}
            displayEmpty
          >
            {TIMEZONES.map((tz) => (
              <MenuItem key={tz} value={tz}>
                {tz}
              </MenuItem>
            ))}
          </Select>

          {/* Failure Strategy */}
          <Typography variant="subtitle2" fontWeight={600} mt={2}>
            Failure Strategy
          </Typography>
          <Select
            fullWidth
            size="small"
            value={data.failure_strategy || "stop_on_failure"}
            onChange={handleFailureStrategyChange}
          >
            {CHECK_SCHEDULE_FAILURE_STRATEGIES.map((strategy) => (
              <MenuItem key={strategy} value={strategy}>
                {FAILURE_STRATEGY_LABELS[strategy]}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          disabled={!isValid || !cronValid || isProcessing}
          loading={isProcessing}
          onClick={() => {
            if (data) {
              const payload: CheckScheduleSchema = {
                name: data.name,
                description: data.description,
                app_config_id: data.app_config_id,
                check_ids: data.check_ids || [],
                cron_expression: data.cron_expression,
                timezone: data.timezone || "UTC",
                enabled: data.enabled !== false,
                failure_strategy: data.failure_strategy || "stop_on_failure",
              };
              onSave(payload);
            }
          }}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckScheduleDialog;
